import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import EsignPageWrapper from "./esign.styles";
import actions from "@redux/esign/actions";
import { useMissingAccountExternalIdRedirect } from "@hooks";
import { esignSchema } from "./esignSchema";
import {
  EsignLandingPage,
  EsignButtons,
  EsignPdfPage,
  EsignThankYouPage,
  EsignForm,
} from "./containers/index";
import { Loader } from "../../components/loader";
import EsignModal from "./containers/esignModal";
import {
  smsName,
  acceptContractAndSchedule,
  FORM_ID_ESIGN,
  ESIGN_DOCUMENT_TYPE,
} from "../../constants/commonConstants";

export const Esign = () => {
  const { queryString } = useSelector((state) => state.StartUp);
  const {
    hasSigned,
    isSubmitting,
    esignFailed,
    currentEsignPageIndex,
    isDownloadingPdf,
  } = useSelector((state) => state.esign);

  const dispatch = useDispatch();
  const { id: accountExternalId } = queryString || {};

  const [activeDocumentType, setActiveDocumentType] = useState(
    ESIGN_DOCUMENT_TYPE.CONTRACT
  );
  const [numPages, setNumPages] = useState(null);
  const [documentsViewed, setDocumentsViewed] = useState([]);
  const [checkboxChecked, setChecboxChecked] = useState(false);

  const bothDocumentsViewed =
    documentsViewed.includes("Conditional Contract") &&
    documentsViewed.includes("Repayment Schedule");

  const handleDocumentNavigation = useCallback((type) => {
    setActiveDocumentType(type);
    setNumPages(null);
  }, []);

  const onDocumentLoadSuccess = useCallback(
    (pdfResponse) => {
      setNumPages(pdfResponse._pdfInfo.numPages);
      const currentDocument =
        activeDocumentType.name === "Contract"
          ? "Conditional Contract"
          : "Repayment Schedule";
      if (!documentsViewed.includes(currentDocument)) {
        setDocumentsViewed((previousState) => {
          return [...previousState, currentDocument];
        });
      }
    },
    [activeDocumentType, documentsViewed]
  );

  const dispatchEsign = useCallback(
    (values) => {
      if (checkboxChecked && bothDocumentsViewed) {
        const smsToken = values[smsName];
        dispatch(actions.submitEsign({ smsToken, accountExternalId }));
      }
    },
    [checkboxChecked, bothDocumentsViewed, accountExternalId, dispatch]
  );

  const handleModalAction = useCallback(() => {
    dispatch(actions.resetEsignSubmit());
  }, [dispatch]);

  useMissingAccountExternalIdRedirect();

  useEffect(() => {
    if (currentEsignPageIndex === 0) {
      setActiveDocumentType(ESIGN_DOCUMENT_TYPE.CONTRACT);
    }
  }, [currentEsignPageIndex, setActiveDocumentType]);

  return (
    <EsignPageWrapper>
      <div className="content-box">
        <Formik
          validateOnChange
          validateOnBlur
          validateOnMount
          initialValues={{
            [smsName]: "",
            [acceptContractAndSchedule]: false,
          }}
          validationSchema={esignSchema}
          onSubmit={(values) => dispatchEsign(values)}
        >
          {({
            values = {},
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            submitForm,
          }) => (
            <main className="esign-page-content">
              <form
                id={FORM_ID_ESIGN}
                noValidate
                autoComplete="off"
                onSubmit={(event) => {
                  event.preventDefault();
                  (() => submitForm())();
                }}
              >
                <div>
                  {currentEsignPageIndex === 0 && <EsignLandingPage />}
                  {(currentEsignPageIndex === 1 ||
                    currentEsignPageIndex === 2) && (
                    <EsignPdfPage
                      activeDocumentType={activeDocumentType}
                      onDocumentLoadSuccess={onDocumentLoadSuccess}
                      handleDocumentNavigation={handleDocumentNavigation}
                      numPages={numPages}
                      accountExternalId={accountExternalId}
                    />
                  )}
                  {currentEsignPageIndex === 3 && bothDocumentsViewed && (
                    <>
                      {isSubmitting ? (
                        <Loader />
                      ) : (
                        <EsignForm
                          isSubmitting={isSubmitting}
                          values={values}
                          errors={errors}
                          touched={touched}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          submitForm={submitForm}
                          setFieldValue={setFieldValue}
                          setChecboxChecked={setChecboxChecked}
                          checkboxChecked={checkboxChecked}
                        />
                      )}
                    </>
                  )}
                  {currentEsignPageIndex === 4 && hasSigned === true && (
                    <EsignThankYouPage />
                  )}
                </div>
              </form>
            </main>
          )}
        </Formik>
        {!isDownloadingPdf && !isSubmitting && (
          <EsignButtons
            className="button-group"
            esignStep={currentEsignPageIndex}
            stepRange={[0, 4]}
            activeDocumentType={activeDocumentType}
            handleDocumentNavigation={handleDocumentNavigation}
          />
        )}
      </div>
      <EsignModal
        visible={esignFailed}
        closeable={false}
        showButton={true}
        actionButtonText="OK, Lets go"
        handleModalAction={handleModalAction}
      >
        Looks like there was a problem with your submission. Please check and
        re-enter the code to continue.
      </EsignModal>
    </EsignPageWrapper>
  );
};
